import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AmplifyModal = ({ whisper, onClose, onAmplify }) => {
    const { appFunctions, setMessage, currentUserProfile } = useAppContext();
    const { t } = useTranslation();
    const [isLoadingStandard, setIsLoadingStandard] = useState(false);
    const [isLoadingSuper, setIsLoadingSuper] = useState(false);

    const standardAmplifyCost = 10;
    const superAmplifyCost = 100;

    const handleAmplify = async (isSuper) => {
        const amountToAmplify = isSuper ? (whisper.echoesInvested || 0) + 100 : standardAmplifyCost;

        if (onAmplify) {
            onAmplify(amountToAmplify);
        }

        if (isSuper) {
            setIsLoadingSuper(true);
        } else {
            setIsLoadingStandard(true);
        }

        try {
            const amplifyFunction = httpsCallable(appFunctions, 'amplifyWhisper');
            
            await amplifyFunction({
                whisperId: whisper.id,
                amount: amountToAmplify
            });

            const successMessage = isSuper ? t('amplifyModal.superAmplifySuccess') : t('amplifyModal.standardAmplifySuccess');
            setMessage(successMessage);
            onClose();
        } catch (error) {
            setMessage(t('amplifyModal.amplifyError', { message: error.message }));
        } finally {
            setIsLoadingSuper(false);
            setIsLoadingStandard(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4" style={{ zIndex: 'var(--z-index-modal-top)' }}>
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-lg w-full text-white relative border border-yellow-500/50">
                <h3 className="text-2xl font-bold mb-4 text-yellow-300 font-playfair text-center">{t('amplifyModal.title')}</h3>
                <p className="text-gray-300 mb-8 text-center">{t('amplifyModal.description')}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Standard Amplify Option */}
                    <div className="border border-gray-700 rounded-lg p-6 flex flex-col items-center text-center">
                        <LucideIcons.Flame size={40} className="text-yellow-400 mb-4" />
                        <h4 className="text-xl font-bold mb-2">{t('amplifyModal.standardAmplify.title')}</h4>
                        <p className="text-gray-400 text-sm mb-4">{t('amplifyModal.standardAmplify.description')}</p>
                        <p className="text-2xl font-bold text-yellow-400 mb-6">{t('amplifyModal.standardAmplify.cost', { count: standardAmplifyCost })}</p>
                        <button 
                            onClick={() => handleAmplify(false)} 
                            disabled={isLoadingStandard || isLoadingSuper || currentUserProfile.tokens < standardAmplifyCost}
                            className="w-full px-4 py-3 bg-yellow-600 text-black font-bold rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50"
                        >
                            {isLoadingStandard ? t('amplifyModal.processing') : t('amplifyModal.standardAmplify.buttonText')}
                        </button>
                    </div>

                    {/* Super Amplify Option */}
                    <div className="border border-purple-500 rounded-lg p-6 flex flex-col items-center text-center ring-2 ring-purple-500/50 shadow-lg">
                        <LucideIcons.Sparkles size={40} className="text-purple-400 mb-4" />
                        <h4 className="text-xl font-bold mb-2 text-purple-300">{t('amplifyModal.superAmplify.title')}</h4>
                        <p className="text-gray-400 text-sm mb-4">{t('amplifyModal.superAmplify.description')}</p>
                        <div className="text-left text-xs text-gray-400 space-y-2 my-4 p-3 bg-gray-800/50 rounded-md">
                            <p><strong className="text-purple-300">{t('amplifyModal.superAmplify.yourPerksTitle')}</strong> {t('amplifyModal.superAmplify.yourPerks')}</p>
                            <p><strong className="text-purple-300">{t('amplifyModal.superAmplify.authorPerksTitle')}</strong> {t('amplifyModal.superAmplify.authorPerks')}</p>
                        </div>
                        <p className="text-2xl font-bold text-purple-300 mb-6">{t('amplifyModal.superAmplify.cost', { count: superAmplifyCost })}</p>
                        <button 
                            onClick={() => handleAmplify(true)} 
                            disabled={isLoadingStandard || isLoadingSuper || currentUserProfile.tokens < superAmplifyCost}
                            className="w-full px-4 py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-500 transition-colors disabled:opacity-50"
                        >
                            {isLoadingSuper ? t('amplifyModal.processing') : t('amplifyModal.superAmplify.buttonText')}
                        </button>
                    </div>
                </div>

                <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition duration-300" aria-label={t('amplifyModal.closeAriaLabel')}>
                    <LucideIcons.X size={20} />
                </button>
            </div>
        </div>,
        document.body
    );
};

export default AmplifyModal;
